import { get } from 'svelte/store';

import type { ApiTypes } from '@api/client';
import { userStore } from '@components/user/store';
import { STATS_ID_MAX_VAL } from '@shared/constants';
import { Cookie } from '@shared/cookies';
import type { HandleClientError } from '@sveltejs/kit';
import { getCookie } from '@utils/cookies';

import { env } from '$env/dynamic/public';

let sentryInitPromise: Promise<void> | null = null;

const initSentry = (user: ApiTypes.UserCurrent) => {
    if (sentryInitPromise || !env.PUBLIC_SENTRY_DSN) return;

    const statsId = Number(getCookie(Cookie.StatsId));
    if (isNaN(statsId) || statsId < 1 || statsId > STATS_ID_MAX_VAL * 0.01) return;

    sentryInitPromise = (async () => {
        const Sentry = await import('@sentry/sveltekit?inline');
        Sentry.init({
            dsn: env.PUBLIC_SENTRY_DSN,
            tracesSampleRate: 1.0,
            sampleRate: 1.0,
            replaysOnErrorSampleRate: 1.0,
            replaysSessionSampleRate: 1.0,
            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.replayIntegration(),
                Sentry.browserProfilingIntegration(),
                Sentry.browserApiErrorsIntegration(),
                Sentry.httpClientIntegration(),
                Sentry.httpContextIntegration(),
            ],
            beforeSend: async (event) => {

                event.user = {
                    id: user.id,
                    username: user.username || user.displayName,
                };
                const { roomStore } = await import('@components/room/store');
                const room = get(roomStore).activeRoom;

                if (room) {
                    const { partnerStore } = await import('@components/room/partner/store');
                    const roomPartner = get(partnerStore);

                    event.contexts = {
                        ...(event.contexts || {}),
                        room: {
                            id: room.id,
                            status: room.status,
                            matchType: room.matchType ? room.matchType[0] : 'unknown',
                            partner: roomPartner?.id,
                        },
                    };
                }
                return event;
            },
        });
    })();
};

const store = get(userStore);
if (store.user) {
    initSentry(store.user);
} else {
    const unsubscribe = userStore.subscribe(state => {
        if (state.user) {
            initSentry(state.user);
            unsubscribe();
        }
    });
}

/** @type {import('@sveltejs/kit').HandleClientError} */
export const handleError: HandleClientError = async (input) => {
    if (!sentryInitPromise) return;

    try {
        await sentryInitPromise;
        const Sentry = await import('@sentry/sveltekit?inline');
        const sentryErrorHandler = Sentry.handleErrorWithSentry();
        return (sentryErrorHandler as HandleClientError)(input);
    } catch {
        return;
    }
};
